import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import {
  Plane, Clock, Briefcase, Heart, Trophy, GraduationCap, Ship,
  ShieldCheck, UserCheck, PlaneLanding, HandCoins, BadgeCheck, Headphones,
  Users, Luggage, Check, Star, ArrowRight, Phone, Mail, MapPin, MessageSquare,
  Smartphone, QrCode, Share, PlusSquare,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import QuoteForm from "@/components/QuoteForm";
import FloatingContact from "@/components/FloatingContact";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const HERO = "https://images.pexels.com/photos/15774577/pexels-photo-15774577.jpeg";
const SKYLINE = "https://images.unsplash.com/photo-1692154600992-463fa9b27abd";
const INTERIOR = "https://images.unsplash.com/photo-1687634366070-c06d3f037154";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, delay: d, ease: "easeOut" },
});

const SERVICES = [
  { icon: Plane, title: "Airport Transfers", desc: "Flat-rate rides to & from IAH and Hobby with live flight tracking." },
  { icon: Clock, title: "Hourly Chauffeur", desc: "A dedicated chauffeur at your disposal, by the hour." },
  { icon: Briefcase, title: "Corporate Travel", desc: "Reliable, discreet transportation for executives and clients." },
  { icon: Heart, title: "Weddings", desc: "Arrive in elegance on your special day with red-carpet service." },
  { icon: Trophy, title: "Sporting Events", desc: "Skip the parking. Ride in style to any Houston venue." },
  { icon: GraduationCap, title: "Proms & Special Events", desc: "Safe, memorable luxury for milestone celebrations." },
  { icon: Ship, title: "Cruise Port Transport", desc: "Seamless transfers to Galveston cruise terminals." },
];

const WHY = [
  { icon: UserCheck, title: "Professional Chauffeurs", desc: "Vetted, uniformed and courteous drivers." },
  { icon: PlaneLanding, title: "Flight Tracking", desc: "We monitor your flight and adjust for delays." },
  { icon: Headphones, title: "Meet & Greet", desc: "Personal greeting at arrivals with a name sign." },
  { icon: HandCoins, title: "Flat-Rate Pricing", desc: "Transparent quotes, no surge, no surprises." },
  { icon: BadgeCheck, title: "Licensed & Insured", desc: "Fully compliant, commercially insured fleet." },
  { icon: ShieldCheck, title: "24/7 Availability", desc: "Book any time — we operate around the clock." },
];

const STEPS = [
  { n: "01", title: "Enter Trip Details", desc: "Tell us your pickup, drop-off, date and vehicle." },
  { n: "02", title: "Receive Instant Quote", desc: "Get a transparent flat-rate price in seconds." },
  { n: "03", title: "Book Securely Online", desc: "Confirm and pay safely with encrypted checkout." },
  { n: "04", title: "Enjoy Your Ride", desc: "Your chauffeur arrives on time. Sit back & relax." },
];

const ROUTES = [
  { from: "Sugar Land", to: "IAH", price: 150, origin: "Sugar Land, TX", destination: "George Bush Intercontinental Airport (IAH), Houston, TX" },
  { from: "Katy", to: "IAH", price: 145, origin: "Katy, TX", destination: "George Bush Intercontinental Airport (IAH), Houston, TX" },
  { from: "Richmond", to: "IAH", price: 160, origin: "Richmond, TX", destination: "George Bush Intercontinental Airport (IAH), Houston, TX" },
  { from: "Downtown Houston", to: "IAH", price: 80, origin: "Downtown Houston, TX", destination: "George Bush Intercontinental Airport (IAH), Houston, TX" },
  { from: "Houston", to: "Galveston", price: 180, origin: "Houston, TX", destination: "Galveston, TX" },
];

const FAQS = [
  { q: "How do airport pickups work?", a: "For arrivals, choose Meet & Greet and your chauffeur will wait inside at baggage claim with a name sign. We track your flight in real time, so delays are handled automatically." },
  { q: "Are child seats available?", a: "Yes. Add a note during booking or call us and we'll provide infant or booster seats at no extra charge." },
  { q: "Is gratuity included?", a: "Gratuity is not built into your quote. Tipping your chauffeur is always appreciated but never expected — you can add an optional tip when you book, or hand it directly to your driver." },
  { q: "Can I cancel?", a: "Absolutely. Manage or cancel your reservation anytime from your customer dashboard. Free cancellation up to 1 hour before pickup." },
  { q: "How far in advance do I need to book?", a: "Reservations must be booked at least 2 hours before your pickup time. For last-minute rides, please call us and we'll do our best to accommodate." },
  { q: "Do you track delayed flights?", a: "We monitor your flight status continuously and adjust your chauffeur's arrival to match, at no additional cost." },
];

function SectionTitle({ eyebrow, title, sub, center }) {
  return (
    <motion.div {...fade()} className={`${center ? "text-center mx-auto" : ""} max-w-2xl mb-14`}>
      <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">{eyebrow}</div>
      <h2 className="text-3xl sm:text-4xl lg:text-5xl text-foreground tracking-tight">{title}</h2>
      {sub && <p className="mt-4 text-muted-foreground leading-relaxed">{sub}</p>}
    </motion.div>
  );
}

export default function Landing() {
  const [vehicles, setVehicles] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [pricing, setPricing] = useState(null); // {reservation_fee, per_mile, min_fare}
  const [routePrices, setRoutePrices] = useState({}); // idx -> {price, miles}
  const routesLib = useMapsLibrary("routes");
  const location = useLocation();

  useEffect(() => {
    const target = location.state?.scrollTo || (location.hash ? location.hash : null);
    if (!target) return;
    const t = setTimeout(() => {
      document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
    }, 100);
    return () => clearTimeout(t);
  }, [location.state, location.hash]);

  useEffect(() => {
    api.get("/quote-options").then((r) => {
      setVehicles(r.data.vehicles);
      const sedan = r.data.vehicles.find((v) => v.id === "sedan") || r.data.vehicles[0];
      if (sedan) setPricing({ reservation_fee: sedan.reservation_fee, per_mile: sedan.per_mile, min_fare: r.data.min_fare });
    }).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!routesLib || !pricing) return;
    const svc = new routesLib.DirectionsService();
    ROUTES.forEach((r, i) => {
      svc.route({ origin: r.origin, destination: r.destination, travelMode: window.google.maps.TravelMode.DRIVING })
        .then((res) => {
          const miles = res.routes[0].legs[0].distance.value / 1609.34;
          const price = Math.max(pricing.reservation_fee + pricing.per_mile * miles, pricing.min_fare);
          setRoutePrices((prev) => ({ ...prev, [i]: { price: Math.round(price), miles: Math.round(miles) } }));
        })
        .catch(() => {});
    });
  }, [routesLib, pricing]);

  const submitContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", contact);
      toast.success("Message sent. We'll be in touch shortly.");
      setContact({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div className="bg-background text-foreground overflow-x-hidden">
      <Navbar />

      {/* HERO */}
      <section id="book" className="relative min-h-screen flex items-center pt-24 pb-16">
        <div className="absolute inset-0">
          <img src={HERO} alt="Luxury chauffeur" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center w-full">
          <motion.div {...fade()}>
            <div className="text-xs uppercase tracking-[0.35em] text-primary mb-5">Houston · Available 24/7</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Luxury Chauffeur<br />Service in <span className="text-primary italic">Houston</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-foreground/70 max-w-md leading-relaxed">
              Flat-rate airport transfers, corporate travel and special-event chauffeurs.
              Impeccable vehicles, professional drivers, effortless booking.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="tel:+19793379282" data-testid="hero-call-btn"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 uppercase tracking-wide text-sm hover:bg-primary/90 transition-colors">
                <Phone size={16} /> Call or Text Now
              </a>
              <button onClick={() => document.querySelector("#fleet")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center gap-2 border border-white/20 px-7 py-3.5 uppercase tracking-wide text-sm hover:border-primary hover:text-primary transition-colors">
                View Fleet <ArrowRight size={16} />
              </button>
            </div>
          </motion.div>
          <motion.div {...fade(0.15)}><QuoteForm /></motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <SectionTitle eyebrow="What We Offer" title="Chauffeured for Every Occasion"
            sub="From airport runs to weddings, 7 Star delivers a premium experience tailored to the moment." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {SERVICES.map((s, i) => (
              <motion.div key={s.title} {...fade(i * 0.05)} data-testid={`service-${i}`}
                className="bg-[#0A0A0A] p-8 hover:bg-[#121212] transition-colors group">
                <s.icon className="text-primary mb-5" size={30} strokeWidth={1.4} />
                <h3 className="text-xl text-foreground mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FLEET */}
      <section id="fleet" className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <SectionTitle eyebrow="The Fleet" title="Immaculate Vehicles, Meticulously Maintained" />
          <div className="grid md:grid-cols-3 gap-8">
            {vehicles.map((v, i) => (
              <motion.div key={v.id} {...fade(i * 0.08)} data-testid={`fleet-${v.id}`}
                className="border border-white/10 bg-[#121212] overflow-hidden group">
                <div className="aspect-[4/3] overflow-hidden">
                  {v.image ? <img src={v.image} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full bg-secondary" />}
                </div>
                <div className="p-6">
                  <h3 className="text-xl text-foreground">{v.name}</h3>
                  {v.model ? <p className="text-sm text-primary mb-4">{v.model}</p> : <div className="mb-4" />}
                  <div className="flex gap-5 text-sm text-muted-foreground mb-4">
                    <span className="flex items-center gap-1.5"><Users size={15} /> {v.pax} pax</span>
                    <span className="flex items-center gap-1.5"><Luggage size={15} /> {v.luggage} bags</span>
                  </div>
                  <ul className="space-y-1.5">
                    {v.amenities.map((a) => (
                      <li key={a} className="flex items-center gap-2 text-sm text-foreground/70"><Check size={14} className="text-primary" /> {a}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY / ABOUT */}
      <section id="why" className="py-24 relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div {...fade()} className="relative">
            <img src={INTERIOR} alt="Luxury interior" className="w-full aspect-[4/3] object-cover border border-white/10" />
          </motion.div>
          <div>
            <SectionTitle eyebrow="Why Choose Us" title="The 7 Star Difference" />
            <div className="grid sm:grid-cols-2 gap-6">
              {WHY.map((w, i) => (
                <motion.div key={w.title} {...fade(i * 0.05)} className="flex gap-4">
                  <w.icon className="text-primary shrink-0" size={24} strokeWidth={1.4} />
                  <div>
                    <h4 className="text-foreground text-lg mb-1">{w.title}</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <SectionTitle eyebrow="Simple & Seamless" title="How It Works" center />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} {...fade(i * 0.08)} className="relative">
                <div className="text-5xl font-heading text-primary/25 mb-3">{s.n}</div>
                <h4 className="text-xl text-foreground mb-2">{s.title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* POPULAR ROUTES / PRICING */}
      <section id="routes" className="py-24 relative">
        <div className="absolute inset-0 opacity-20">
          <img src={SKYLINE} alt="Houston skyline" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-background/80" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <SectionTitle eyebrow="Flat Airport Rates" title="Popular Routes" center
            sub="Starting fares for our Black Sedan, calculated live by driving distance via Google Maps. Tip is optional at checkout." />
          <div className="border border-white/10 divide-y divide-white/10 bg-[#121212]/80 backdrop-blur">
            {ROUTES.map((r, i) => (
              <motion.div key={i} {...fade(i * 0.05)} data-testid={`route-${i}`}
                className="flex items-center justify-between px-6 py-5 hover:bg-white/5 transition-colors">
                <div className="flex items-center gap-3 text-foreground">
                  <span>{r.from}</span>
                  <ArrowRight size={16} className="text-primary" />
                  <span>{r.to}</span>
                  {routePrices[i]?.miles ? <span className="text-xs text-muted-foreground ml-2">· {routePrices[i].miles} mi</span> : null}
                </div>
                <div className="text-primary font-heading text-2xl">
                  ${r.price}<span className="text-xs text-muted-foreground font-body">+</span>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button data-testid="routes-book-btn" onClick={() => document.querySelector("#book")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wide h-12 px-8">
              Book This Route
            </Button>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-24 bg-[#0A0A0A] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <SectionTitle eyebrow="Testimonials" title="What Our Clients Say" center />
          <div className="grid md:grid-cols-3 gap-8">
            {reviews.map((r, i) => (
              <motion.div key={r.id} {...fade(i * 0.08)} data-testid={`review-${i}`}
                className="border border-white/10 bg-[#121212] p-8">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: r.rating }).map((_, k) => <Star key={k} size={16} className="fill-primary text-primary" />)}
                </div>
                <p className="text-foreground/80 leading-relaxed italic">&ldquo;{r.text}&rdquo;</p>
                <div className="flex items-center gap-3 mt-6">
                  <img src={r.image} alt={r.name} className="w-11 h-11 rounded-full object-cover border border-primary/40" />
                  <div>
                    <div className="text-foreground text-sm">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GET THE APP */}
      <section id="get-app" className="py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fade(0)}>
              <div className="inline-flex items-center gap-2 text-primary text-xs uppercase tracking-[0.2em] mb-4">
                <Smartphone size={15} /> Install our app
              </div>
              <h2 className="text-3xl sm:text-4xl text-foreground mb-5">Take 7 Star Limo <span className="text-primary">with you</span></h2>
              <p className="text-muted-foreground leading-relaxed mb-8 max-w-md">
                Add our app to your phone&apos;s home screen and book a ride in two taps &mdash; no App Store, no download wait. Same instant quotes, saved trips, and one-tap booking.
              </p>
              <ul className="space-y-3 mb-8">
                {["Book & get an instant price in seconds", "Track your upcoming reservations", "Opens full-screen like a native app"].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-foreground/85">
                    <Check size={18} className="text-primary shrink-0" /> <span className="text-sm">{t}</span>
                  </li>
                ))}
              </ul>
              <div className="grid sm:grid-cols-2 gap-4 max-w-lg">
                <div className="border border-white/10 bg-[#121212] p-4" data-testid="install-android-card">
                  <div className="text-primary text-xs uppercase tracking-[0.15em] mb-2 flex items-center gap-2"><Smartphone size={14} /> Android</div>
                  <p className="text-sm text-muted-foreground">Open in Chrome, tap the menu and choose <span className="text-foreground">&ldquo;Install app&rdquo;</span> (or tap the Install App button).</p>
                </div>
                <div className="border border-white/10 bg-[#121212] p-4" data-testid="install-ios-card">
                  <div className="text-primary text-xs uppercase tracking-[0.15em] mb-2 flex items-center gap-2"><Share size={14} /> iPhone</div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 flex-wrap">Tap <Share size={13} className="text-foreground inline" /> Share, then <PlusSquare size={13} className="text-foreground inline" /> <span className="text-foreground">&ldquo;Add to Home Screen&rdquo;</span>.</p>
                </div>
              </div>
            </motion.div>

            <motion.div {...fade(0.15)} className="flex justify-center">
              <div className="border border-primary/30 bg-[#121212] p-8 text-center shadow-[0_8px_40px_rgba(0,0,0,0.6)]" data-testid="app-qr-card">
                <div className="bg-white p-4 inline-block">
                  <img src="/qr-app.png" alt="Scan to open 7 Star Limo on your phone" className="w-52 h-52" data-testid="app-qr-image" />
                </div>
                <div className="flex items-center justify-center gap-2 text-foreground mt-5">
                  <QrCode size={16} className="text-primary" />
                  <span className="text-sm uppercase tracking-[0.15em]">Scan with your phone</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Point your camera at the code to open the app, then tap Install.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          <SectionTitle eyebrow="Good to Know" title="Frequently Asked Questions" center />
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                <AccordionTrigger data-testid={`faq-${i}`} className="text-left text-foreground hover:text-primary hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 bg-[#0A0A0A] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14">
          <div>
            <SectionTitle eyebrow="Get In Touch" title="Contact 7 Star"
              sub="Questions or custom requests? Reach out any time — our team is available 24/7." />
            <ul className="space-y-5 text-foreground/80">
              <li className="flex items-center gap-4"><Phone className="text-primary" size={20} /> <a href="tel:+19793379282" className="hover:text-primary">979-337-9282</a></li>
              <li className="flex items-center gap-4"><Mail className="text-primary" size={20} /> <a href="mailto:trips.7starlimo@gmail.com" className="hover:text-primary">trips.7starlimo@gmail.com</a></li>
              <li className="flex items-center gap-4"><MapPin className="text-primary" size={20} /> Serving Greater Houston & surrounding areas</li>
              <li className="flex items-center gap-4"><Clock className="text-primary" size={20} /> Open 24 hours · 7 days a week</li>
            </ul>
            <a href="tel:+19793379282" data-testid="contact-call-btn"
              className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-4 uppercase tracking-wide text-sm hover:bg-primary/90 transition-colors">
              <MessageSquare size={16} /> Call or Text Now
            </a>
          </div>
          <form onSubmit={submitContact} data-testid="contact-form" className="border border-white/10 bg-[#121212] p-8 space-y-5">
            <Input required data-testid="contact-name" placeholder="Your name" value={contact.name}
              onChange={(e) => setContact({ ...contact, name: e.target.value })} className="bg-secondary border-white/10 rounded-none h-12" />
            <Input required type="email" data-testid="contact-email" placeholder="Email address" value={contact.email}
              onChange={(e) => setContact({ ...contact, email: e.target.value })} className="bg-secondary border-white/10 rounded-none h-12" />
            <Input data-testid="contact-phone" placeholder="Phone (optional)" value={contact.phone}
              onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="bg-secondary border-white/10 rounded-none h-12" />
            <Textarea required data-testid="contact-message" placeholder="How can we help?" rows={4} value={contact.message}
              onChange={(e) => setContact({ ...contact, message: e.target.value })} className="bg-secondary border-white/10 rounded-none" />
            <Button type="submit" disabled={sending} data-testid="contact-submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wide h-12">
              {sending ? "Sending..." : "Send Message"}
            </Button>
          </form>
        </div>
      </section>

      <Footer />
      <FloatingContact />
    </div>
  );
}
